import { Organization } from "../../../../models/Organization";
import { Project } from "../../../../models/Project";
import { Box, Link, TextField, Typography, useTheme } from "@mui/material";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import { SettingsSwitchOptionWrapper } from "../../../../components/form/OptionsWrapper";
import { useRevalidator } from "react-router-dom";
import React, { useContext, useState } from "react";
import { FormButtons } from "../../../../components/form/FormButtons";
import { ApiClient } from "../../../../client/ApiClient";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { providerCustomization } from "../AuthProvider";

interface Props {
  organization: Organization
  project: Project
}

interface State {
  claims: string
}

export const SessionTokenCustomization = (props: Props) => {
  const revalidator = useRevalidator()
  const { organization, project } = props
  const theme = useTheme()
  const [state, setState] = useState<State>({
    claims: project.session?.custom_claims ?? ""
  })
  const [isLoading, setIsLoading] = useState(false)
  const { error, success } = useContext(SnackbarContext)

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await ApiClient.updateProject(organization.id, project.id, {
        session: {
          ...project.session,
          custom_claims: state.claims,
        }
      })

      revalidator.revalidate()
      success("Custom claims updated")
    } catch (e) {
      error("Failed to update custom claims")
    }
    setIsLoading(false)
  }

  const onCancel = () => {
    setState({
      claims: project.session?.custom_claims ?? ""
    })
  }

  const somethingChanged = () => {
    return state.claims !== project.session?.custom_claims
  }

  return <Box>
    <SettingsSwitchOptionWrapper sx={{
      borderBottom: `1px solid ${theme.palette.grey["900"]}`
    }}>
      <SettingsHead title="Customize session token" description="Define additional custom claims for your users' session tokens.
Enter your claims in YAML format. The claims will be included in the JWT issued after a successful authentication."
                    sx={{ mb: 1 }} />
    </SettingsSwitchOptionWrapper>
    <form onSubmit={onSubmit}>
      { /* TODO: change with some editor library which supports yaml e.g. monaco-editor, codemirror, ... */ }
      <TextField multiline fullWidth minRows={10} maxRows={50} InputLabelProps={{
        shrink: true,
      }} name="claims" label="Claims" placeholder={"user_id: \n  test: test"} onChange={onChange} value={state.claims} />
      <Typography sx={{mt: 5}}>Need help? Check out the <Link target="_blank"
                                                 href="https://docs.hanko.io/guides/sessions/session-management#session-token-customization">docs</Link>.</Typography>
      <FormButtons onCancel={onCancel} loading={isLoading} disabled={!somethingChanged()} sx={{mt: 5}} />
    </form>
  </Box>
}